import { useState } from 'react';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { useMutation } from 'react-query';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@tigergraph/app-ui-lib/modal';
import { Input } from '@tigergraph/app-ui-lib/input';
import { FormatValidator } from '@tigergraph/tools-models/utils';
import { AxiosError } from 'axios';
import ErrorMessage from '../ErrorMessage';
import ConfirmButtons from '@/components/ConfirmButtons';
import { expand } from 'inline-style-expand-shorthand';
import { showToast } from '@/components/styledToasterContainer';
import { KIND } from 'baseui/toast';
import { ID_TOKEN_KEY, useWorkspaceContext } from '@/contexts/workspaceContext';
import { createGraph } from '@tigergraph/tools-models';
import trackUtil, { EventStatus } from '@/utils/analytics-service';

export interface CreateNewGraphProps {
  isOpen: boolean;
  message?: string;
  onClose: (graphName: string) => void;
}

export default function CreateNewGraphModal(props: CreateNewGraphProps) {
  const [css] = useStyletron();
  const { isOpen, message, onClose } = props;
  const { setCurrentGraph, currentWorkspace, refetchSimpleAuth } = useWorkspaceContext();
  const [graphName, setGraphName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { mutate: mutateCreateGraph, isLoading } = useMutation(
    ['createGraph'],
    async (graphName: string) => {
      const response = await createGraph(
        {
          GraphName: graphName,
          VertexTypes: [],
          EdgeTypes: [],
        },
        {
          baseURL: `https://${currentWorkspace?.nginx_host}`,
          version: currentWorkspace?.tg_version,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem(ID_TOKEN_KEY)}`,
          },
        }
      );
      return response;
    },
    {
      onSuccess: () => {
        trackUtil.trackGraphCreation(EventStatus.Success);
        handleClose();
        refetchSimpleAuth();
        setCurrentGraph(graphName);
        showToast({
          kind: KIND.positive,
          message: `Successfully created graph ${graphName}.`,
        });
      },
      onError: (error: AxiosError<{ message: string }>) => {
        trackUtil.trackGraphCreation(EventStatus.Fail);
        setErrorMessage(error.response?.data?.message);
        showToast({
          kind: KIND.negative,
          message: `Failed to create graph ${graphName}\n${error.response?.data?.message}`,
        });
      },
    }
  );

  const handleClose = () => {
    onClose(graphName);
    setGraphName('');
    setErrorMessage(undefined);
  };

  return (
    <div
      className={css({
        display: 'flex',
        width: '100%',
      })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !errorMessage) {
          mutateCreateGraph(graphName);
        }
      }}
    >
      <Modal
        onClose={handleClose}
        isOpen={isOpen}
        overrides={{
          Root: {
            style: {
              zIndex: 2,
            },
          },
        }}
      >
        <ModalHeader
          className={css({
            fontFamily: 'Urbanist',
            fontSize: '16px',
            fontWeight: 600,
            ...expand({
              padding: '0',
            }),
          })}
        >
          Create New Graph
        </ModalHeader>
        <ModalBody>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              rowGap: '8px',
            })}
          >
            {message && (
              <div
                className={css({
                  fontSize: '14px',
                  fontWeight: 400,
                })}
              >
                {message}
              </div>
            )}
            <div
              className={css({
                fontSize: '14px',
                fontWeight: 400,
              })}
            >
              Graph Name
            </div>
            <Input
              aria-label="graph-name-input"
              value={graphName}
              error={!!errorMessage}
              disabled={isLoading}
              onChange={(event) => {
                setGraphName(event.currentTarget.value);
                const formatCheck = FormatValidator.isName(event.currentTarget.value);
                if (!formatCheck.success) {
                  setErrorMessage(formatCheck.message);
                } else {
                  setErrorMessage(undefined);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !errorMessage) {
                  mutateCreateGraph(graphName);
                }
                e.stopPropagation();
              }}
              overrides={{
                Root: {
                  style: {
                    ...expand({
                      borderRadius: '2px',
                    }),
                  },
                },
              }}
            />
            {errorMessage && <ErrorMessage message={errorMessage} />}
          </div>
        </ModalBody>
        <ModalFooter>
          <ConfirmButtons
            confirmLabel={'Done'}
            cancelLabel={'Cancel'}
            confirmLoading={isLoading}
            cancelDisabled={isLoading}
            confirmDisabled={!!errorMessage}
            onConfirm={() => {
              mutateCreateGraph(graphName);
            }}
            onCancel={handleClose}
          />
        </ModalFooter>
      </Modal>
    </div>
  );
}
